import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { tagStyle } from '@utils/tag-color.util';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Observable, firstValueFrom, map, startWith } from 'rxjs';

import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';

import {
  UserTypeViewModel,
  TenantViewModel,
  RoleViewModel,
  DeviceTag,
} from '../account-list.component';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import {
  AccountEditorDialogViewModel,
  AccountFormGroup,
  EditedAccountViewModel,
} from './account-edit-dialog.model';

@Component({
  selector: 'app-account-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
  ],
  templateUrl: './account-edit-dialog.component.html',
  styleUrl: './account-edit-dialog.component.scss',
})
export class AccountEditDialogComponent {
  public readonly tagStyle = tagStyle;
  /** 更新中 */
  public updating: boolean;

  /** ログインユーザーの権限 */
  public loggedInUserRole: RoleViewModel;

  /** ユーザー種別 */
  public userType: UserTypeViewModel;

  /** テナント一覧 */
  public tenantList: TenantViewModel[];

  /** 現在のテナント */
  public currentTenant: TenantViewModel | undefined;

  /** フォームグループ */
  public formGroup: AccountFormGroup;

  /** フィルターされたデバイスタグ一覧 */
  public filteredDeviceTagList: Observable<DeviceTag[]>;

  /** デバイスキーワードフォームコントロール */
  public deviceKeywordCtrl: FormControl<string | null> = new FormControl('');

  /** デバイスタグ一覧 */
  private deviceTagList: DeviceTag[];

  /** 権限名マップ */
  private readonly roleNameMap: Record<UserTypeViewModel, string> = {
    system_admin: 'マクニカ管理者',
    tenant_admin: 'テナント管理者',
    tenant_user: 'テナントユーザー',
  };

  /** カナ入力のバリデーションルール
   *  全角カタカナ、半角カタカナ、半角英数字、半角記号、全角記号を許容する
   *  半角記号は、!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~
   *  全角記号は、！＂＃＄％＆＇（）＊＋，－．／：；＜＝＞？＠［＼］＾＿｀｛｜｝〜
   */
  private readonly pattern =
    /^[ァ-ヶー\uFF65-\uFF9FA-Za-z0-9!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~！＂＃＄％＆＇（）＊＋，－．／：；＜＝＞？＠［＼］＾＿｀｛｜｝〜]+$/;

  constructor(
    @Inject(MAT_DIALOG_DATA) options: AccountEditorDialogViewModel,
    private dialogRef: MatDialogRef<AccountEditDialogComponent>,
    private dialog: MatDialog,
  ) {
    this.updating = options.updating;
    this.loggedInUserRole = options.loggedInUserRole;
    this.userType = options.userType;
    this.tenantList = options.tenantList || [];
    this.deviceTagList = options.deviceTagList || [];
    this.currentTenant = options.currentTenant;

    this.formGroup = new FormGroup({
      familyName: new FormControl(options.target?.familyName || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(32)],
      }),
      givenName: new FormControl(options.target?.givenName || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(32)],
      }),
      familyNameKana: new FormControl(options.target?.familyNameKana || '', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.maxLength(32),
          Validators.pattern(this.pattern),
        ],
      }),
      givenNameKana: new FormControl(options.target?.givenNameKana || '', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.maxLength(32),
          Validators.pattern(this.pattern),
        ],
      }),
      email: new FormControl(options.target?.email || '', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.email,
          Validators.maxLength(128),
        ],
      }),
      mfa: new FormControl<boolean>(
        options.target?.mfa == null ? true : options.target.mfa,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
    });

    this.filteredDeviceTagList = this._getFilteredDeviceTagList();
    this.filteredDeviceTagList

    if (this.userType === 'tenant_admin') {
      const nonAdminTenantId =
        this.currentTenant?.tenantId !== 'SYSTEM-ADMIN'
          ? this.currentTenant?.tenantId
          : '';
      this.formGroup.addControl(
        'tenantId',
        new FormControl<string>(
          {
            value: options.target?.tenantId || nonAdminTenantId || '',
            disabled: this.updating, // mat-selectはHTML側ではdisabledにできないため
          },
          {
            nonNullable: true,
            validators: [Validators.required],
          },
        ),
      );
    } else if (this.userType === 'tenant_user') {
      this.formGroup.addControl(
        'configurable',
        new FormControl<boolean>(options.target?.configurable ?? true, {
          nonNullable: true,
          validators: [Validators.required],
        }),
      );
      this.formGroup.addControl(
        'deviceTagList',
        // 配列は値型のためコピーを作成(作成しないと編集中のタグと一覧画面のタグが同期してしまう)
        new FormControl(
          options.target && options.target.deviceTagList
            ? [...options.target.deviceTagList]
            : [],
          {
            nonNullable: true,
          },
        ),
      );
    }

    // フォームの状態を更新
    this.formGroup.updateValueAndValidity();
  }

  /**
   * デバイスタグを選択した一覧から削除する
   * @param deviceTag 削除するデバイスタグ
   */
  public removeDeviceTag(deviceTag: string): void {
    const deviceTagList = this.formGroup.get('deviceTagList')?.value || [];
    const index = deviceTagList.indexOf(deviceTag);

    if (index >= 0) {
      deviceTagList.splice(index, 1);
      this.formGroup.get('deviceTagList')?.setValue(deviceTagList);
    }
  }

  /**

   * デバイスタグを選択した場合の挙動
   * @param event 選択したイベント
   */
  public onSelectedDeviceTag(event: MatAutocompleteSelectedEvent): void {
    const deviceTagList = this.formGroup.get('deviceTagList')?.value || [];
    deviceTagList.push(event.option.value);
    this.formGroup.get('deviceTagList')?.setValue(deviceTagList);

    this.deviceKeywordCtrl.setValue(null);
  }

  /**
   * 送信
   */
  public async submit() {
    const familyName = this.formGroup.get('familyName')?.value || '';
    const givenName = this.formGroup.get('givenName')?.value || '';
    const operation = this.updating ? '変更' : '新規登録';
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `アカウント「${familyName} ${givenName}」を${operation}してよろしいですか？`,
      operation: operation,
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    const account: EditedAccountViewModel = {
      familyName: this.formGroup.get('familyName')?.value || '',
      givenName: this.formGroup.get('givenName')?.value || '',
      familyNameKana: this.formGroup.get('familyNameKana')?.value || '',
      givenNameKana: this.formGroup.get('givenNameKana')?.value || '',
      email: this.formGroup.get('email')?.value || '',
      mfa: this.formGroup.get('mfa')?.value,
      tenantId:
        this.formGroup.get('tenantId')?.value ||
        this.currentTenant?.tenantId ||
        '',
      configurable: this.formGroup.get('configurable')?.value,
      deviceTagList: this.formGroup.get('deviceTagList')?.value || [],
    };
    this.dialogRef.close(account);
  }

  /**
   * 権限名
   */
  public get roleName(): string {
    return this.roleNameMap[this.userType] || '';
  }

  /**
   * デバイスタグ一覧を絞り込む
   * @param value 値
   * @returns 絞り込み結果
   */
  private _filter(value: string): DeviceTag[] {
    const filterValue = value.toLowerCase();
    return this.deviceTagList.filter((tag) =>
      tag.tagName.toLowerCase().includes(filterValue),
    ).sort((first, second) => (first.tagName < second.tagName) ? -1 : 1);
  }

  /**
   * 絞り込み後のデバイスタグ一覧を取得する
   */
  private _getFilteredDeviceTagList() {
    return this.deviceKeywordCtrl.valueChanges.pipe(
      startWith(null),
      map((tag: string | null) =>
        tag ? this._filter(tag) : this.deviceTagList.slice().sort((first, second) => (first.tagName < second.tagName) ? -1 : 1),
      ),
    );
  }
}
